import React from 'react';
import PageLoader from '../../../common/PageLoader';
import { applySort, SORT, useUserCoords } from '../../../../utils/sortListings';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.css';
import {
  getCarDealerListing,
  getCarDealerListingFiltered,
} from './../../../../Redux/Actions/UserAction';
import { useTranslation } from 'react-i18next';
import SortControl from '../../../common/SortControl';
import CarFilterPanel, {
  EMPTY_CAR_FILTER,
  isCarFilterActive,
} from '../../../common/CarFilterPanel';
export default function CarDealers() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  
  // Get current language for RTL support
  const isRTL = i18n.language === 'ar';
  
  // [CF 2026-08-17] The Arabic-detection and text-align helpers that lived here
  // had one caller between them \u2014 the card name, which is now centred by the
  // stylesheet like every other category.

  const [sortOption, setSortOption] = useState(SORT.DISTANCE); // [CF 2026-08-02] nearest-first by default
  // [CF 2026-08-02] Only asks the browser for a location when the user
  // actually picks the distance sort; cached for the session after that.
  const userCoords = useUserCoords(sortOption === SORT.DISTANCE);
  // [CF 2026-08-02] Dealer car filter — applied on the SERVER (see
  // getCarDealerListingFiltered), because dealer rows carry no car data.
  const [carFilter, setCarFilter] = useState({ ...EMPTY_CAR_FILTER });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const home = useSelector((state) => state?.home);
  const CarDealertList = home?.CarDealertList;

  useEffect(() => {
    if (isCarFilterActive(carFilter)) {
      dispatch(getCarDealerListingFiltered(carFilter));
    } else {
      dispatch(getCarDealerListing());
    }
  }, [dispatch, carFilter]);

  const visibleList = React.useMemo(() => {
    if (!CarDealertList) return undefined;
    let list = [...CarDealertList];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((x) =>
        (x.carDealerName || '').toLowerCase().includes(q)
      );
    }
    list = applySort(list, sortOption, {
      name: (x) => x.carDealerName,
      coords: userCoords,
    });
    return list;
  }, [CarDealertList, sortOption, userCoords, search]);


  /* [CF 2026-08-17] Car Dealers was the only listing that loaded with a shimmer
     of 12 placeholder cards. Now uses the shared PageLoader like every other
     screen — see components/common/PageLoader.js for why there were several. */
  if (!CarDealertList) {
    return (
      <div className="text-center text-warning" style={{ padding: '80px' }}>
        <PageLoader />
      </div>
    );
  }

  // If no car details found after loading
  /* [CF 2026-08-09] An early `return` used to replace the WHOLE page with a
     "nothing found" panel whenever the list came back empty — the title, the
     search box and the filter panel went with it. On the server-filtered
     categories (Car Dealers, Rent a Car) that is a dead end: filter down to
     zero results and the controls you need to widen the filter have vanished,
     so the only way out is the browser Back button. The normal render path
     already shows an in-layout empty message beneath the header, so the early
     return is gone and that message does the job with the controls on screen.
  */
  return (
    <div>
      <div className="main-content-area">
        <div className="container">
          <h2 className="mb-4">{t('Car Dealers')}</h2>

          <div
            className="filter-row mb-4 d-flex align-items-center flex-nowrap"
            style={{ gap: '14px' }}
          >
            {/* Sort By — left */}
            <SortControl value={sortOption} onChange={setSortOption} />

            {/* [CF 2026-08-02] Filter funnel — panel opens on its own row below. */}
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-label={t('Filter')}
              title={t('Filter')}
              style={{
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '50%',
                background: isCarFilterActive(carFilter) ? '#fff7e6' : 'transparent',
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
            >
              <img
                src="assets/images/icons/filter.png"
                alt={t('Filter')}
                style={{ width: '22px', height: '22px' }}
              />
            </button>

            {/* Search (icon → expands) + result count — fills remaining space on the same row */}
            <div
              className="d-flex align-items-center"
              style={{
                gap: '12px',
                minWidth: 0,
                flex: '1 1 auto',
                justifyContent: 'flex-end',
              }}
            >
              {searchOpen ? (
                <div
                  style={{
                    position: 'relative',
                    flex: '1 1 auto',
                    maxWidth: '460px',
                    minWidth: 0,
                  }}
                >
                  <input
                    type="text"
                    autoFocus
                    className="form-control"
                    placeholder={t('Search by name')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ paddingInlineEnd: '34px' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setSearchOpen(false);
                    }}
                    aria-label={t('Clear')}
                    style={{
                      position: 'absolute',
                      insetInlineEnd: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      color: '#666',
                    }}
                  >
                    &times;
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  aria-label={t('Search by name')}
                  style={{
                    width: '42px',
                    height: '42px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: '50%',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <img src="assets/images/icons/search.png" alt={t('Search by name')} style={{ width: '22px', height: '22px' }} />
                </button>
              )}
            </div>
          </div>

          {filtersOpen && (
            <CarFilterPanel
              value={carFilter}
              onChange={setCarFilter}
              onApply={() => setFiltersOpen(false)}
              hide={['fuelTypeId', 'cyclinderId']}
            />
          )}

          {visibleList && visibleList.length === 0 ? (
            <p className="text-muted">{t('No results match your search.')}</p>
          ) : (
            <div className="row">
              {(visibleList || []).map((x, i) => {
                // Show the business LOGO on the card, not the first banner/gallery
                // image. Fall back to the first media only if there is no logo.
                const logo =
                  x?.businessMedia?.find(
                    (media) => media?.mediaType === 'LOGO'
                  )?.media?.path || x?.businessMedia?.[0]?.media?.path;
                return (
                  <div
                    key={x?.carDealerId || i}
                    className="col-4 col-sm-4 col-md-4 col-lg-3 mb-4"
                  >
                    <div className="user-car-dealers-block">
                      <Link to={`/user-car-dealers-details/${x?.carDealerId}`}>
                        <div
                          className="line-a"
                          style={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            overflow: 'hidden',
                            borderRadius: '50%',   // [CF] client: all logos rounded
                            background: '#f4f4f4',
                          }}
                        >
                          <img
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            src={
                              logo ||
                              'assets/images/user/garages/garage-logo/a.jpg'
                            }
                            alt={x?.carDealerName || ''}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                'assets/images/user/garages/garage-logo/a.jpg';
                            }}
                          />
                        </div>
                        {/* [CF 2026-08-17] Centre the name under its logo, the
                            way Garages, Car Wash and Taxi already do.
                            `.user-car-dealers-block` has centred its text all
                            along — an inline text-align on this one line was
                            overriding it, so dealer names hung to the left of a
                            centred circle while every other category lined up. */}
                        <div className="line-b">{x?.carDealerName}</div>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
